<template>
  <card-container
    :id="pageId"
    :loading-message="status.loadingMessage"
    :class="pageId"
    :is-loading="status.isLoading"
    :disabled="isSubmitBtnDisabled"
    :no-action="isHideActionBtns"
    @submit="submitForm"
    @reset="status.activeStep === OrderRideStep.OrderRide ? cancelRideEstimation() : resetForm()"
  >
    <template #title>
      Order Ride Now
    </template>

    <template #card-notes>
      <FirstRideOrderNotify v-if="isNeedToCallUsToOrderFirstRide" />
    </template>

    <template #content>
      <div
        class="row"
        :class="{ 'align-items-center': !isPhoneNumberVisible }"
      >
        <label class="col-sm-3 control-label"> Preferred Contact Number </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="orderRideRequest.riderPhone"
            :disabled="!isEditableForm"
            :options="contactPhoneNumbers"
          />
          <phone-number-input
            v-if="isPhoneNumberVisible"
            id="contact-phone"
            v-model="newPhoneNumber"
            class="mt-3"
            :page-id="pageId"
            :disabled="!isEditableForm"
          >
            <template #help-block>
              <span
                v-if="status.preferredContactWarning"
                class="error-message"
              >
                {{ status.preferredContactWarning }}
              </span>
            </template>
          </phone-number-input>
        </div>
      </div>

      <go-go-google-map
        map-type-id="roadmap"
        class="mt-10 mobile-mt-10"
        :markers="[markers[0]]"
        :map-center="getMapCenter(0)"
        :is-draggable="isEditableForm"
        :zoom="18"
        collapse-title="Pickup/Start Location"
        @change="setMarkerPosition"
      />

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Pickup/Start Location </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="pickupLocationId"
            :disabled="!isEditableForm"
            :options="addressList"
          />
        </div>
      </div>

      <expand-address-section
        v-if="isStartAddressFormVisible"
        v-model="startAddress"
        show-location-name
        :parent-id="pageId"
        :is-editing="isEditableForm"
        :show-geocode="false"
        :show-home-note="false"
        :show-gourmet-note="false"
        :show-map="false"
        :show-apt="false"
        :show-google-place-id="false"
        :anchor-bound="endAddressAnchorBound"
        :index="0"
        :is-venues-search="isPartnerDashboard"
        @set-notes="setNotes"
      />

      <div class="row justify-content-center mt-20 mb-10">
        <p-button
          :id="`btnSwitch-${pageId}`"
          type="purple"
          round
          @click="swapAddress"
        >
          Switch&nbsp;&nbsp;
          <i
            class="fa fa-exchange"
            style="transform: rotate(90deg)"
          />
        </p-button>
      </div>

      <go-go-google-map
        map-type-id="roadmap"
        class="mt-20 mobile-mt-10"
        :markers="[markers[1]]"
        :map-center="getMapCenter(1)"
        :is-draggable="isEditableForm"
        :zoom="18"
        collapse-title="DropOff/End Location"
        @change="setMarkerPosition"
      />

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> DropOff/End Location </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="dropoffLocationId"
            :disabled="!isEditableForm"
            :options="addressList"
          />
        </div>
      </div>

      <expand-address-section
        v-if="isEndAddressFormVisible"
        v-model="endAddress"
        show-location-name
        :parent-id="pageId"
        :is-editing="isEditableForm"
        :show-geocode="false"
        :show-home-note="false"
        :show-gourmet-note="false"
        :show-map="false"
        :show-apt="false"
        :show-google-place-id="false"
        :anchor-bound="startAddressAnchorBound"
        :index="1"
        :is-venues-search="isPartnerDashboard"
        @set-notes="setNotes"
      />

      <div
        v-if="availablePartnerRidePurposes.length > 0"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Ride Purpose* </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="orderRideRequest.partnerRidePurpose"
            :disabled="!isEditableForm"
            :options="availablePartnerRidePurposes"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Type of Car* </label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="orderRideRequest.typeOfCar"
            :disabled="!isEditableForm"
            :options="availableRideTypes"
            required
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-5"> Note to Driver </label>
        <div class="col-sm-8">
          <div
            v-if="pickupDropoffNotes"
            class="pickup-dropoff-notes"
          >
            {{ pickupDropoffNotes }}
          </div>
          <sanitized-textarea
            id="noteToDriver"
            v-model="orderRideRequest.noteToDriver"
            autosize
            :disabled="!isEditableForm"
          />
          <span class="help-block"> This note will be sent to the driver. </span>
        </div>
      </div>

      <div
        v-if="isPartnerDashboard"
        class="row"
      >
        <div class="col-sm-8 offset-sm-3">
          <div>
            <el-checkbox
              id="isExpandDriverSearch"
              v-model="isExpandDriverSearch"
              :disabled="!isEditableForm"
            >
              Expand Driver Search
            </el-checkbox>
          </div>

          <div>
            <el-checkbox
              id="isExpandVehicleSearch"
              v-model="isExpandVehicleSearch"
              :disabled="!isEditableForm"
            >
              Expand Vehicle Search
            </el-checkbox>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-8 offset-sm-3">
          <div>
            <el-checkbox
              id="noteGroceries"
              v-model="hasGroceries"
              :disabled="!isEditableForm"
            >
              The rider has groceries.
            </el-checkbox>
          </div>

          <div>
            <el-checkbox
              id="noteLuggage"
              v-model="hasLuggage"
              :disabled="!isEditableForm"
              class="pb-0"
            >
              The rider has luggage.
            </el-checkbox>
          </div>
        </div>
      </div>

      <div
        v-if="isPartnerDashboard"
        class="row"
      >
        <div class="col-sm-9 offset-sm-3">
          <div>
            <el-checkbox
              id="isDoNotApplySubsidy"
              v-model="orderRideRequest.rideType.isDoNotApplySubsidy"
              class="pb-0 font-weight-bold"
            >
              Do not apply partner subsidy to this ride
            </el-checkbox>
            <div class="help-block pt-0">
              This option should only be selected if the customer wants to pay for the full cost of the ride and does not want it to count
              towards their subsidized rides.
            </div>
          </div>
        </div>
      </div>

      <template v-if="!isRiderPhoneNumberValid">
        <hr>
        <div class="pb-10 text-center text-red font-weight-bold">
          Preferred contact number is needed before getting an estimate.
        </div>
      </template>

      <message-html
        :result-html="status.resultHtml"
        :is-top-line="true"
      />

      <trip-exists-error
        v-if="status.isShowTripExistsError"
        :ride-info="existsRideInfo"
      />

      <template v-if="estimationRide && status.activeStep === OrderRideStep.OrderRide">
        <hr>
        <PartnerEstimateTabs
          :tabs="partnerEstimateTabs"
          :selected-index="selectedPartnerEstimateIndex"
          :all-partner-estimates="allPartnerEstimates"
          :selected-partner-estimate="selectedPartnerEstimate"
          @select="onSelectPartnerEstimateTab"
        />
        <EstimationAssistantRide
          v-if="isAssistRideType"
          v-model:operatorConfirmedSurgePriceWithCustomer="operatorConfirmedSurgePriceWithCustomer"
          :estimation-ride="estimationRide"
          :preferred-contact-warning="status.preferredContactWarning"
          :ride-types="availableRideTypes"
          @choose-estimate="onChooseEstimate"
        />
        <EstimationRideView
          v-else
          v-model:operatorConfirmedSurgePriceWithCustomer="operatorConfirmedSurgePriceWithCustomer"
          :estimation-ride="estimationRide"
          :preferred-contact-warning="status.preferredContactWarning"
          :show-full-detail="!isHideRideEstimateFullDetail"
        />
      </template>

      <template v-if="status.activeStep === OrderRideStep.WaitForDriver">
        <div
          id="waitForDriverMatchSection"
          class="ride-state"
        >
          <div class="row justify-content-center">
            <p class="col-12 text-center">
              Thanks! We are requesting a ride for you.
            </p>
            <p class="col-12 text-center">
              This process can take up to two minute.
            </p>
            <p class="col-12 text-center notification-message mt-5 mobile-mt-5">
              <b>PLEASE DO NOT REFRESH THE PAGE.</b>
            </p>
          </div>
        </div>
      </template>

      <template v-if="status.activeStep === OrderRideStep.RideStatus">
        <message-html
          v-if="rideStatus.message"
          :is-top-line="true"
          :is-bottom-line="false"
          :result-html="rideStatus.message"
        />

        <template v-else>
          <hr>
          <ordered-ride-status :ride-status="rideStatus" />
        </template>

        <div class="form-submit-button-group">
          <hr>
          <p-button
            type="purple"
            round
            @click="resetSteps"
          >
            OK
          </p-button>
        </div>
      </template>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>

    <template #reset-button>
      {{ resetBtnText }}
    </template>
  </card-container>

  <will-call-rides-for-order
    v-if="showWillCallRides && isPhoneNumberValid"
    :is-disabled="!isEditableForm"
    :is-loading="status.isLoading"
    :request-id="rideStatus.requestId"
    :phone-number="rideAvailablePhoneNumber"
    :selected-will-call-ride-id="selectedWillCallRideId"
    @copy-to-form="onCopyWillCallRideToForm"
  />
</template>

<script setup lang="ts">
import { ref, toRefs, Ref, computed, watch, reactive, defineAsyncComponent, onMounted, nextTick } from 'vue';
import _ from 'lodash';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { sendPost } from '@/Utils/api';

import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { usePartner } from '@/use/partner';
import { useAddress } from '@/use/address';
import { useSwalDialog } from '@/use/swalDialog';
import { useReturnTripAlert } from '@/use/returnTripAlert';
import { useRide } from '@/use/ride';
import { usePartnerEstimates } from '@/use/partnerEstimates';
import { callerService } from '@/services/caller';

import { RideType } from '@/models/rideType';
import { MedicalTripResult, OrderDataType, RideServiceType } from '@/models/ride';
import { CustomLocationType, AddressType } from '@/models/address';
import { useRideTypes } from '@/use/rideTypes';
import type { ScheduledRide } from '@/models/scheduledRide';

const TripExistsError = defineAsyncComponent(() => import('@/components/UIComponents/OrderingForms/TripExistsError.vue') as any);
const EstimationAssistantRide = defineAsyncComponent(() => import('@/components/UIComponents/OrderingForms/EstimationAssistantRide.vue') as any);
const EstimationRideView = defineAsyncComponent(() => import('@/components/UIComponents/OrderingForms/EstimationRide.vue') as any);
const OrderedRideStatus = defineAsyncComponent(() => import('@/components/UIComponents/OrderingForms/OrderedRideStatus.vue') as any);
const FirstRideOrderNotify = defineAsyncComponent(() => import('./FirstRideOrderNotify.vue') as any);
const WillCallRidesForOrder = defineAsyncComponent(
  () => import('@/components/Main/Operator/RideOrdering/WillCallRidesForOrder.vue') as any,
);
const PartnerEstimateTabs = defineAsyncComponent(
  () => import('@/components/UIComponents/OrderingForms/PartnerEstimateTabs.vue') as any,
);

const pageId = 'family-order-ride';

enum OrderRideStep {
  GettingEstimation = 0,
  OrderRide = 1,
  RideStatus = 2,
  WaitForDriver = 3,
}

const props = withDefaults(
  defineProps<{
    hasCompletedRide?: boolean;
  }>(),
  {
    hasCompletedRide: true,
  },
);

const { isFamilyDashboard, isPartnerDashboard } = useDashboard();
const {
  customerInfo,
  customerPartnerId,
  rideAvailablePhoneNumber,
  customerStatus,
  contactPhoneNumbers,
  addressList,
  customerPartner,
  customerPartnerCode,
  availablePartnerRidePurposes,
  isHideRideEstimateFullDetail,
} = useCustomer();
const { hasUnresolvedDisputes } = toRefs(customerStatus);
const { updateAddressWithGeocode, trimAddress, getAnchorBound } = useAddress();
const { showConfirmPopupDialog, showWarningPopupDialog } = useSwalDialog();
const { showReturnTripAlertIfNeeded } = useReturnTripAlert();
const { partnerId, partnerCode } = usePartner();
const { getOrderRideTypes, getAvailableRideType } = useRideTypes();
const { rideOrderData, checkMedicalTripForDiamondBarPartnership, checkMedicalTripForRotcPartnership } = useRide();

const orderRideRequest: Ref<any> = ref({
  phoneNumber: rideAvailablePhoneNumber.value,
  riderPhone: rideAvailablePhoneNumber.value,
  partnerRidePurpose: '',
  typeOfCar: '',
  serviceType: RideServiceType.Transportation,
  noteToDriver: '',
  rideType: {
    badDrivers: false,
    overrideBigCar: false,
    overrideSmallCar: false,
    overrideBannedCar: false,
    isDoNotApplySubsidy: false,
  },
  isMedicalTrip: false,
});
const status = reactive({
  loadingMessage: '',
  isLoading: false,
  resultHtml: '',
  activeStep: OrderRideStep.GettingEstimation,
  preferredContactWarning: '',
  isShowTripExistsError: false,
});
const newPhoneNumber = ref('');
const pickupLocationId = ref('home');
const dropoffLocationId = ref('home');
const hasGroceries = ref(false);
const hasLuggage = ref(false);

const startAddress: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress1: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
});
const endAddress: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress1: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
  type: false,
});

const markers: Ref<any> = ref([
  {
    position: {
      lat: 0.0,
      lng: 0.0,
    },
    initialPosition: {
      lat: 0.0,
      lng: 0.0,
    },
    index: 0,
    isDraggable: true,
    isShow: false,
    icon: '/static/img/pickup_marker.png',
  },
  {
    position: {
      lat: 0.0,
      lng: 0.0,
    },
    initialPosition: {
      lat: 0.0,
      lng: 0.0,
    },
    index: 1,
    isDraggable: true,
    isShow: false,
    icon: '/static/img/dropoff_marker.png',
  },
]);

const existsRideInfo: Ref<any> = ref(null);
const estimationRide = ref({
  firstName: '',
  lastName: '',
  rideType: '',
  surgeFactor: '',
  eta: '',
  duration: '',
  distance: '',
  lowEstimate: '',
  highEstimate: '',
  callRideFromEstimateLink: '',
  credits: '',
  partnerSubsidy: '',
  partnerSubsidyEstimate: null,
  isNoReturnTrip: false,
  returnTripEtaMins: undefined,
  riderName: '',
  etaText: '',
  estimate: '',
  warningMessage: '',
  isAutoTip: false,
  lowAutoTipAmount: 0,
  highAutoTipAmount: 0,
});
const rideStatus = ref({
  requestId: null,
  rideStatus: '',
  eta: '',
  driverName: '',
  driverPhone: '',
  driverPlate: '',
  carBrand: '',
  carModel: '',
  carColor: '',
  carImage: '',
  mapUrl: '',
  startLocation: '',
  endLocation: '',
  createdAt: '',
  txtCarColor: '',
  car: '',
  message: '',
});
const operatorConfirmedSurgePriceWithCustomer = ref(false);
const isExpandDriverSearch = ref(false);
const isExpandVehicleSearch = ref(false);
const {
  allPartnerEstimates,
  selectedPartnerEstimateIndex,
  partnerEstimateTabs,
  selectedPartnerEstimate,
  setPartnerEstimatesFromResponse,
  resetPartnerEstimates,
  selectPartnerEstimateTab,
  getOrderingPartnerId,
} = usePartnerEstimates({
  onClear: () => {
    operatorConfirmedSurgePriceWithCustomer.value = false;
  },
});

const driverNotePickUp = ref('');
const driverNoteDropOff = ref('');

const preferredVendorRideTypes = computed(() => customerPartner.value?.preferredVendorRideTypes || []);
const availableRideTypes = computed(() =>
  getOrderRideTypes(customerPartnerCode.value || partnerCode.value, preferredVendorRideTypes.value),
);
const isNeedToCallUsToOrderFirstRide = computed(() => isFamilyDashboard.value && !props.hasCompletedRide);

const isEditableForm = computed(() => {
  if (isNeedToCallUsToOrderFirstRide.value) {
    return false;
  }

  return status.activeStep === OrderRideStep.GettingEstimation;
});
const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(rideAvailablePhoneNumber.value));
const selectedWillCallRideId = ref<number | null>(null);
const showWillCallRides = computed(
  () =>
    isPartnerDashboard.value &&
    !!customerPartner.value?.config?.allowWillCallRides,
);
const isPhoneNumberVisible = computed(() => !orderRideRequest.value.riderPhone);
const isStartAddressFormVisible = computed(() => pickupLocationId.value === '');
const isEndAddressFormVisible = computed(() => dropoffLocationId.value === '');

const startAddressAnchorBound = computed(() => getAnchorBound(startAddress.value));
const endAddressAnchorBound = computed(() => getAnchorBound(endAddress.value));

const isRiderPhoneNumberValid = computed(() => util.isPhoneNumberValid(orderRideRequest.value.riderPhone || newPhoneNumber.value));
const isAssistRideType = computed(() => orderRideRequest.value.typeOfCar === RideType.UberAssist.key)

const isSubmitBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (!customerInfo.value.id) {
    return true;
  }

  if (!isRiderPhoneNumberValid.value) {
    return true;
  }

  if (status.activeStep === OrderRideStep.OrderRide && !estimationRide.value.callRideFromEstimateLink) {
    return true;
  }

  return false;
});

const isHideActionBtns = computed(() => {
  if (isNeedToCallUsToOrderFirstRide.value) {
    return true;
  }

  if (status.activeStep === OrderRideStep.GettingEstimation) {
    return false;
  }

  if (status.activeStep === OrderRideStep.OrderRide) {
    return false;
  }

  return true;
});

const submitBtnText = computed(() => {
  if (status.activeStep === OrderRideStep.GettingEstimation) {
    return 'Get Estimation';
  }

  return 'Order Ride';
});

const resetBtnText = computed(() => {
  if (status.activeStep === OrderRideStep.GettingEstimation) {
    return 'Reset';
  }

  return 'Cancel Ride';
});

const pickupDropoffNotes = computed(() => util.makePickupDropoffDriverNotes(driverNotePickUp.value, driverNoteDropOff.value));

const setMarkerPosition = (
  lat: number | string | null,
  lng: number | string | null,
  index: number,
  isMarkerChanged: boolean = true,
  isAddressChanged: boolean = false,
) => {
  util.updateMarkerPosition(markers.value[index], lat, lng, isAddressChanged);

  if (!isMarkerChanged) {
    return;
  }

  if (index === 0) {
    startAddress.value = updateAddressWithGeocode(startAddress.value, lat, lng);
  } else if (index === 1) {
    endAddress.value = updateAddressWithGeocode(endAddress.value, lat, lng);
  }
};

const getMapCenter = (index: number) => {
  return {
    lat: markers.value[index]?.position?.lat || 37.42248,
    lng: markers.value[index]?.position?.lng || -122.08547,
  };
};

const checkPreferredNumber = async () => {
  status.preferredContactWarning = '';

  if (!isPhoneNumberVisible.value) {
    // if user choose the phone number from the dropdown, we don't need to check preferred contact number
    return;
  }

  if (!isRiderPhoneNumberValid.value) {
    return;
  }

  try {
    const response = await callerService.getCallerLovedOneIdName(newPhoneNumber.value);

    status.preferredContactWarning = util.checkAndGetBelongsWarningMessage(customerInfo.value, response, {
      isFamilyDashboard: isFamilyDashboard.value,
      partnerId: partnerId.value,
    });

    if (response) {
      const text = isFamilyDashboard.value
        ? 'Please make sure this is the phone number your loved one can use to talk to their driver!'
        : 'Please make sure this is the phone number the user wants for this ride!';

      showWarningPopupDialog({
        text,
        title: status.preferredContactWarning,
      });
    }
  } catch (error) {
    // No need to do anything
  }
};

const checkMedicalTripForPartnerships = async () => {
  orderRideRequest.value.isMedicalTrip = false;
  status.loadingMessage = 'Checking medical trip for partnership';
  status.isLoading = true;

  const resultDiamondBarPartnership = await checkMedicalTripForDiamondBarPartnership(startAddress.value, endAddress.value);
  const resultRotcPartnership = await checkMedicalTripForRotcPartnership(startAddress.value, endAddress.value);

  status.isLoading = false;

  if (
    resultDiamondBarPartnership === MedicalTripResult.VALIDATION_FAILED ||
    resultRotcPartnership === MedicalTripResult.VALIDATION_FAILED
  ) {
    return false;
  }

  if (resultDiamondBarPartnership === MedicalTripResult.USER_DECLINED || resultRotcPartnership === MedicalTripResult.USER_DECLINED) {
    orderRideRequest.value.rideType.isDoNotApplySubsidy = true;
  }

  if (
    resultDiamondBarPartnership === MedicalTripResult.VALIDATION_PASSED ||
    resultRotcPartnership === MedicalTripResult.VALIDATION_PASSED
  ) {
    orderRideRequest.value.isMedicalTrip = true;
  }

  return true;
};

const getPostData = () => {
  trimAddress(startAddress.value);
  trimAddress(endAddress.value);

  let postData: any = rideUtility.makeEstimationPostData(
    {
      ...orderRideRequest.value,
      startAddress: startAddress.value,
      endAddress: endAddress.value,
      phoneNumber: rideAvailablePhoneNumber.value,
      pickupDropoffNotes: pickupDropoffNotes.value,
    },
    startAddress.value.streetAddress,
    endAddress.value.streetAddress,
    isPartnerDashboard.value,
  );

  if (!postData.riderPhone) {
    postData.riderPhone = newPhoneNumber.value;
  }

  if (markers.value[1].isShow) {
    postData.endCoors = {
      lat: endAddress.value.latitude,
      lng: endAddress.value.longitude,
    };
  }

  postData.callerId = customerInfo.value?.id;
  postData.willcallRideId = selectedWillCallRideId.value;

  if (isPartnerDashboard.value) {
    postData.partnerId = partnerId.value;
  }

  return postData;
};

const applyEstimationData = async (estimationData: any, { showWarnings = true }: { showWarnings?: boolean } = {}) => {
  estimationRide.value = rideUtility.parseEstimationRide({
    estimationData,
  });

  if (showWarnings) {
    await showReturnTripAlertIfNeeded(estimationRide.value.isNoReturnTrip, estimationRide.value.returnTripEtaMins);
  } else if (estimationRide.value?.warningMessage) {
    showWarningPopupDialog({
      text: estimationRide.value.warningMessage,
    });
  }
};

const onSelectPartnerEstimateTab = (index: number) => selectPartnerEstimateTab(index, applyEstimationData);

const getWebEstimate = async () => {
  const postData = getPostData();
  status.resultHtml = '';
  existsRideInfo.value = null;
  status.isShowTripExistsError = false;
  status.loadingMessage = 'Getting Estimation...';
  status.isLoading = true;

  try {
    const apiName = isAssistRideType.value ? 'getWebEstimateOptions' : 'getWebEstimate';
    const response = await sendPost(apiName, postData);
    const estimationData = setPartnerEstimatesFromResponse(response.data);

    await applyEstimationData(estimationData);

    status.activeStep = OrderRideStep.OrderRide;
  } catch (error: any) {
    if (error.response && error.response.data) {
      const rideStatus = error.response.data.rideStatus;
      if (rideStatus) {
        fillAndDisplayRideDetails(error.response.data);
      } else if (error.response.data.code === 'current_trip_exists') {
        existsRideInfo.value = error.response.data.rideData;
        status.isShowTripExistsError = true;
      } else {
        status.resultHtml = util.getErrorResponse(error);
      }
    } else {
      status.resultHtml = util.getErrorResponse(error);
    }
  }
  status.isLoading = false;
};

const fillAndDisplayRideDetails = (json: any) => {
  const {
    eta,
    driverName,
    driverPhone,
    driverPlate,
    startLocation,
    destination,
    endLocation,
    createdAt,
    carImage,
    message,
    rideStatus: orderRideStatus,
    carBrand = '',
    carModel = '',
    carColor = '',
    mapUrl = null,
    requestId = null,
  } = json;

  rideStatus.value = {
    requestId,
    eta,
    driverName,
    driverPhone,
    driverPlate,
    carBrand,
    carModel,
    carColor,
    carImage,
    mapUrl,
    startLocation,
    createdAt,
    rideStatus: orderRideStatus,
    endLocation: destination || endLocation,
    txtCarColor: carColor,
    car: `${carColor} ${carBrand} ${carModel}`,
    message: message && util.setSuccessMessage(message),
  };

  status.activeStep = OrderRideStep.RideStatus;
};

const cancelRideEstimation = async () => {
  const isCancelRideEstimation = await showConfirmPopupDialog({
    title: 'Cancel this ride?',
    confirmButtonText: 'Cancel',
  });

  if (!isCancelRideEstimation) {
    return;
  }

  status.activeStep = OrderRideStep.GettingEstimation;
  status.preferredContactWarning = '';
  resetPartnerEstimates();
};

const orderRide = async () => {
  if (hasUnresolvedDisputes.value) {
    showWarningPopupDialog({
      text: 'Services on this account cannot be ordered because there are credit card disputes!',
    });

    return;
  }

  status.activeStep = OrderRideStep.WaitForDriver;

  let callRideFromEstimateLink = operatorConfirmedSurgePriceWithCustomer.value
    ? `${estimationRide.value.callRideFromEstimateLink}&operatorConfirmedSurgePriceWithCustomer=true`
    : estimationRide.value.callRideFromEstimateLink;

  if (orderRideRequest.value.isMedicalTrip) {
    const isMedicalTripParam = 'isMedicalTrip';

    if (!callRideFromEstimateLink.includes(isMedicalTripParam)) {
      callRideFromEstimateLink += `&${isMedicalTripParam}=true`;
    }
  }

  const orderingPartnerId = getOrderingPartnerId(partnerId.value || customerPartnerId.value);

  const separatorIndex = callRideFromEstimateLink.indexOf('?');
  if (separatorIndex !== -1) {
    const basePath = callRideFromEstimateLink.substring(0, separatorIndex);
    const params = new URLSearchParams(callRideFromEstimateLink.substring(separatorIndex + 1));
    if (orderingPartnerId != null) {
      params.set('partnerId', String(orderingPartnerId));
    } else {
      params.delete('partnerId');
    }
    callRideFromEstimateLink = `${basePath}?${params.toString()}`;
  } else if (orderingPartnerId != null) {
    callRideFromEstimateLink += `?partnerId=${orderingPartnerId}`;
  }

  try {
    status.loadingMessage = 'Ordering Ride...';
    status.isLoading = true;
    const response = await sendPost(callRideFromEstimateLink, {});

    fillAndDisplayRideDetails(response.data);
  } catch (error: any) {
    if (error.response && error.response.data) {
      const responseStatus = error.response.data.status || error.response.status;
      // uber surge handle
      if (responseStatus === 504) {
        status.activeStep = OrderRideStep.GettingEstimation;
        status.resultHtml = util.getErrorResponse({
          message: 'We are still waiting for a driver to accept. Please check the Live Ride Map page to get the ride status.',
        });
      } else {
        const rideStatus = error.response.data.rideStatus;
        if (rideStatus) {
          fillAndDisplayRideDetails(error.response.data);
        } else {
          status.activeStep = OrderRideStep.GettingEstimation;
          status.resultHtml = util.getErrorResponse(error);
        }
      }
    } else {
      status.activeStep = OrderRideStep.GettingEstimation;
      status.resultHtml = util.getErrorResponse(error);
    }
  }
  status.isLoading = false;
};

const submitForm = async () => {
  if (status.activeStep === OrderRideStep.GettingEstimation) {
    checkPreferredNumber();

    const resultMedicalTrip = await checkMedicalTripForPartnerships();

    if (!resultMedicalTrip) {
      return;
    }

    return getWebEstimate();
  }

  orderRide();
};

const resetForm = () => {
  orderRideRequest.value = {
    riderPhone: rideAvailablePhoneNumber.value,
    partnerRidePurpose: '',
    typeOfCar: getAvailableRideType({
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    }),
    serviceType: RideServiceType.Transportation,
    noteToDriver: '',
    rideType: {
      badDrivers: false,
      overrideBigCar: false,
      overrideSmallCar: false,
      overrideBannedCar: false,
      isDoNotApplySubsidy: false,
    },
    isMedicalTrip: false,
  };

  pickupLocationId.value = 'home';
  dropoffLocationId.value = 'home';
  driverNotePickUp.value = '';
  driverNoteDropOff.value = '';
  newPhoneNumber.value = '';
  hasGroceries.value = false;
  hasLuggage.value = false;

  existsRideInfo.value = null;

  status.resultHtml = '';
  status.activeStep = OrderRideStep.GettingEstimation;
  status.preferredContactWarning = '';
  status.isShowTripExistsError = false;
  selectedWillCallRideId.value = null;
  resetPartnerEstimates();
};

const setNotes = (noteToDriver: string, venuesInstruction: string, locationIndex: number) => {
  if (locationIndex === AddressType.PickUp) {
    driverNotePickUp.value = noteToDriver;
  }

  if (locationIndex === AddressType.DropOff) {
    driverNoteDropOff.value = noteToDriver;
  }
};

const resetSteps = () => {
  status.resultHtml = '';
  status.activeStep = OrderRideStep.GettingEstimation;
  status.isShowTripExistsError = false;
  selectedWillCallRideId.value = null;
  resetPartnerEstimates();
};

const prefillOrderData = async (data: OrderDataType | null) => {
  if (!data) {
    orderRideRequest.value.typeOfCar = getAvailableRideType({
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    });
    return;
  }

  orderRideRequest.value = {
    phoneNumber: rideAvailablePhoneNumber.value,
    riderPhone: data.riderPhone || rideAvailablePhoneNumber.value,
    partnerRidePurpose: data.partnerRidePurpose || '',
    typeOfCar: getAvailableRideType({
      rideType: data.typeOfCar,
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    }),
    serviceType: RideServiceType.Transportation,
    noteToDriver: data.noteToDriver || '',
    rideType: {
      badDrivers: data.rideType?.badDrivers || false,
      overrideBigCar: data.rideType?.overrideBigCar || false,
      overrideSmallCar: data.rideType?.overrideSmallCar || false,
      overrideBannedCar: data.rideType?.overrideBannedCar || false,
      isDoNotApplySubsidy: data.rideType?.isDoNotApplySubsidy || false,
    },
  };

  pickupLocationId.value = '';
  dropoffLocationId.value = '';

  driverNotePickUp.value = '';
  driverNoteDropOff.value = '';

  await nextTick();

  startAddress.value = data.startAddress
    ? _.cloneDeep(data.startAddress)
    : {
        streetAddress: '',
        streetAddress1: '',
        city: '',
        state: '',
        zip: '',
        name: '',
        latitude: null,
        longitude: null,
        googlePlaceId: null,
      };
  endAddress.value = data.endAddress
    ? _.cloneDeep(data.endAddress)
    : {
        streetAddress: '',
        streetAddress1: '',
        city: '',
        state: '',
        zip: '',
        name: '',
        latitude: null,
        longitude: null,
        googlePlaceId: null,
      };
};

const copyFromWillCallRide = async (row: ScheduledRide) => {
  const startAddressData: CustomLocationType = {
    streetAddress: (row.streetAddress1 as string) || '',
    streetAddress1: '',
    city: (row.city1 as string) || '',
    state: (row.state1 as string) || '',
    zip: (row.zip1 as string) || '',
    name: (row.locationName1 as string) || '',
    latitude: row.lat1 != null ? Number(row.lat1) : null,
    longitude: row.long1 != null ? Number(row.long1) : null,
    googlePlaceId: null,
  };
  const endAddressData: CustomLocationType = {
    streetAddress: (row.streetAddress2 as string) || '',
    streetAddress1: '',
    city: (row.city2 as string) || '',
    state: (row.state2 as string) || '',
    zip: (row.zip2 as string) || '',
    name: (row.locationName2 as string) || '',
    latitude: row.lat2 != null ? Number(row.lat2) : null,
    longitude: row.long2 != null ? Number(row.long2) : null,
    googlePlaceId: null,
  };
  await prefillOrderData({
    riderPhone: (row.riderPhone as string) || rideAvailablePhoneNumber.value,
    typeOfCar: (row.rideType as string) || '',
    noteToDriver: (row.noteToDriver as string) || '',
    startAddress: startAddressData,
    endAddress: endAddressData,
  });
};

const onCopyWillCallRideToForm = (row: ScheduledRide) => {
  selectedWillCallRideId.value = row.id as number;
  copyFromWillCallRide(row);
};

const swapAddress = async () => {
  const hasPickup = Boolean(pickupLocationId.value);
  const hasDropoff = Boolean(dropoffLocationId.value);
  if (hasPickup && hasDropoff) {
    [pickupLocationId.value, dropoffLocationId.value] = [dropoffLocationId.value, pickupLocationId.value];
  } else {
    const [tempPickupId, tempDropoffId] = [pickupLocationId.value, dropoffLocationId.value];
    const [tempStart, tempEnd] = [_.cloneDeep(startAddress.value), _.cloneDeep(endAddress.value)];
    pickupLocationId.value = hasDropoff ? tempDropoffId : '';
    dropoffLocationId.value = hasPickup ? tempPickupId : '';

    await nextTick();

    startAddress.value = tempEnd;
    endAddress.value = tempStart;
  }
};

const onChooseEstimate = (selectedEstimationRide: any) => {
  estimationRide.value.callRideFromEstimateLink = selectedEstimationRide.callRideFromEstimateLink
}

watch(hasLuggage, (value: boolean) => {
  if (value) {
    orderRideRequest.value.noteToDriver += orderRideRequest.value.noteToDriver ? ' ' : '';
    orderRideRequest.value.noteToDriver += 'The rider has luggage.';
  } else {
    const pattern = /The rider has luggage./gi;
    orderRideRequest.value.noteToDriver = orderRideRequest.value.noteToDriver?.replace(pattern, '');
  }
});

watch(hasGroceries, (value: boolean) => {
  if (value) {
    orderRideRequest.value.noteToDriver += orderRideRequest.value.noteToDriver ? ' ' : '';
    orderRideRequest.value.noteToDriver += 'The rider has groceries.';
  } else {
    const pattern = /The rider has groceries./gi;
    orderRideRequest.value.noteToDriver = orderRideRequest.value.noteToDriver?.replace(pattern, '');
  }
});

watch(
  pickupLocationId,
  async (value: string) => {
    const addressItem = addressList.value.find((item: any) => item.key === value);
    startAddress.value = {
      streetAddress: addressItem?.address?.streetAddress || '',
      streetAddress1: '',
      city: addressItem?.address?.city || '',
      state: addressItem?.address?.state || '',
      zip: addressItem?.address?.zip || '',
      name: addressItem?.address?.name || '',
      latitude: addressItem?.address?.latitude ? Number(addressItem?.address?.latitude) : null,
      longitude: addressItem?.address?.longitude ? Number(addressItem?.address?.longitude) : null,
      googlePlaceId: addressItem?.address?.googlePlaceId,
    };
  },
  {
    immediate: true,
  },
);

watch(
  startAddress,
  (value: any, oldValue: any) => {
    const isAddressChanged = util.isAddressChanged(value, oldValue);

    const { latitude, longitude } = value;

    setMarkerPosition(Number(latitude), Number(longitude), 0, false, isAddressChanged);
  },
  {
    immediate: true,
  },
);

watch(
  dropoffLocationId,
  async (value: string) => {
    const addressItem = addressList.value.find((item: any) => item.key === value);
    endAddress.value = {
      streetAddress: addressItem?.address?.streetAddress || '',
      streetAddress1: '',
      city: addressItem?.address?.city || '',
      state: addressItem?.address?.state || '',
      zip: addressItem?.address?.zip || '',
      name: addressItem?.address?.name || '',
      latitude: addressItem?.address?.latitude ? Number(addressItem?.address?.latitude) : null,
      longitude: addressItem?.address?.longitude ? Number(addressItem?.address?.longitude) : null,
      googlePlaceId: addressItem?.address?.googlePlaceId,
    };
  },
  {
    immediate: true,
  },
);

watch(
  endAddress,
  (value: any, oldValue: any) => {
    const isAddressChanged = util.isAddressChanged(value, oldValue);

    const { latitude, longitude } = value;

    setMarkerPosition(Number(latitude), Number(longitude), 1, false, isAddressChanged);
  },
  {
    immediate: true,
  },
);

watch(
  availableRideTypes,
  () => {
    orderRideRequest.value.typeOfCar = getAvailableRideType({
      rideTypes: availableRideTypes.value,
      partnerCode: customerPartnerCode.value,
      customerInfo: customerInfo.value,
    });
  },
  {
    immediate: true,
  },
);

watch(isExpandDriverSearch, (value: boolean) => {
  orderRideRequest.value.rideType.badDrivers = value;
});

watch(isExpandVehicleSearch, (value: boolean) => {
  orderRideRequest.value.rideType.overrideBigCar = value;
  orderRideRequest.value.rideType.overrideSmallCar = value;
  orderRideRequest.value.rideType.overrideBannedCar = value;
});

onMounted(() => {
  prefillOrderData(rideOrderData.value);
});
</script>

<style lang="scss">
.family-order-ride {
  .pickup-dropoff-notes {
    padding: 5px 10px;
    background: #f4f3ef;
    font-size: 14px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    color: #000;
  }
}
</style>
